/**
 * Lazy Hover Video - a product card's video is created and loaded only when the card is hovered.
 *
 * Product videos are full clips (around a minute, 13 MB and up at the rendition Liquid exposes), so
 * nothing is fetched ahead of time: no preloading as cards near the viewport, and no hover binding
 * on touch screens, where a tap also fires mouseenter on its way to the product page.
 */
(function() {
    'use strict';

    // Store for loaded videos to prevent duplicate loading
    const loadedVideos = new WeakSet();

    const canHover = window.matchMedia('(hover: hover)').matches;

    /**
     * Initialize a video element inside the wrapper
     */
    function initializeVideo(wrapper) {
        if (loadedVideos.has(wrapper)) return wrapper.querySelector('video');

        const videoSrc = wrapper.dataset.videoSrc;
        const videoType = wrapper.dataset.videoType || 'video/mp4';

        if (!videoSrc) {
            console.warn('Lazy hover video: No video source found');
            return null;
        }

        // Create video element
        const video = document.createElement('video');
        video.muted = true;
        video.setAttribute('muted', ''); // For Safari
        video.loop = true;
        video.playsInline = true;
        // play() pulls what it needs; 'auto' would let the browser take the whole file.
        video.preload = 'metadata';

        // Create source element
        const source = document.createElement('source');
        source.src = videoSrc;
        source.type = videoType;
        video.appendChild(source);

        // Handle successful load
        video.addEventListener('loadeddata', function() {
            wrapper.classList.add('video-ready');
        }, { once: true });

        // Handle errors gracefully
        video.addEventListener('error', function(e) {
            console.error('Lazy hover video: Error loading', videoSrc, e);
            wrapper.classList.add('video-error');
        }, { once: true });

        // Append to wrapper (after the fallback image)
        wrapper.appendChild(video);
        loadedVideos.add(wrapper);

        return video;
    }

    /**
     * Play video on hover - called with the wrapper element
     */
    function handleHoverStart(wrapper) {
        let video = wrapper.querySelector('video');

        // Initialize video if not already done
        if (!video) {
            video = initializeVideo(wrapper);
        }

        if (!video) return;

        // Rejects if the pointer leaves first (pause below) or autoplay is blocked - both fine.
        video.play().catch(function() {});
    }

    /**
     * Pause video when hover ends
     */
    function handleHoverEnd(wrapper) {
        const video = wrapper.querySelector('video');

        if (video) {
            video.pause();
        }
    }

    /**
     * Initialize all lazy hover videos
     */
    function initLazyHoverVideos() {
        const lazyVideos = document.querySelectorAll('[data-video-lazy]:not(.lazy-video-initialized)');

        lazyVideos.forEach(function(wrapper) {
            wrapper.classList.add('lazy-video-initialized');

            // Find the parent card element - this is what receives hover events
            const parentCard = wrapper.closest('.card-media') || wrapper.closest('.card') || wrapper.closest('.card-wrapper');

            if (!parentCard) return;

            // Add hover event listeners to the PARENT CARD (not the hidden wrapper)
            if (canHover) {
                parentCard.addEventListener('mouseenter', function() {
                    handleHoverStart(wrapper);
                });

                parentCard.addEventListener('mouseleave', function() {
                    handleHoverEnd(wrapper);
                });
            }

            // For touch devices - watch for is-tapped class
            const observer = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    if (mutation.attributeName === 'class') {
                        if (parentCard.classList.contains('is-tapped')) {
                            handleHoverStart(wrapper);
                        } else {
                            handleHoverEnd(wrapper);
                        }
                    }
                });
            });
            observer.observe(parentCard, { attributes: true, attributeFilter: ['class'] });
        });
    }

    /**
     * Initialize on DOM ready
     */
    function init() {
        initLazyHoverVideos();

        // Re-initialize when new content is added (e.g., infinite scroll, AJAX)
        if ('MutationObserver' in window) {
            const bodyObserver = new MutationObserver(function(mutations) {
                let shouldInit = false;
                mutations.forEach(function(mutation) {
                    if (mutation.addedNodes.length) {
                        mutation.addedNodes.forEach(function(node) {
                            if (node.nodeType === 1) {
                                if (node.matches && node.matches('[data-video-lazy]')) {
                                    shouldInit = true;
                                } else if (node.querySelector && node.querySelector('[data-video-lazy]')) {
                                    shouldInit = true;
                                }
                            }
                        });
                    }
                });
                if (shouldInit) {
                    initLazyHoverVideos();
                }
            });

            bodyObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Also handle dynamic section rendering in Shopify theme editor
    document.addEventListener('shopify:section:load', initLazyHoverVideos);
})();
